import React, { useEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import Constants from '../../../app/utils/constants'
import starIcon from '../../../assets/images/icons/staricon.svg'
import GroupIcon from '../../../assets/images/icons/Group.svg'
import targetIcon from '../../../assets/images/upskill/target.svg'
import fullStackIcon from '../../../assets/images/upskill/fullstack.svg'
import frontendIcon from '../../../assets/images/upskill/frontend.svg'
import backendIcon from '../../../assets/images/upskill/backend.svg'
import colors from '../../theme/color'
import { City, State } from 'country-state-city'
import { toast } from 'react-toastify'

type UpskillCourse = {
  id: string
  title: string
  subtitle: string
  weeks: string
  level: string
  rating: string
  masteryPoints: string[]
  pkg: string
  projects: string
  status: string
}

const upskillCourses = Constants.upskillCourses as UpskillCourse[]
const upskillCoursesLabels = Constants.upskillCoursesLabels
const upskillCourseTitles = upskillCourses.map((course) => course.title)
const indianStates = State.getStatesOfCountry('IN')

type EnrollFormData = {
  name: string
  phone: string
  email: string
  course: string
  state: string
  city: string
  message: string
}

type EnrollFormErrors = Partial<Record<'name' | 'phone' | 'email' | 'course' | 'state' | 'city', string>>

const stackItemVariants = {
  hidden: { opacity: 0, y: 140, scale: 0.96 },
  show: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      delay: index * 0.16,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
}

const CourseIcon = ({ course }: { course: UpskillCourse }) => {
  const title = course.title.toLowerCase()

  let icon = targetIcon
  if (title.includes('full stack')) icon = fullStackIcon
  if (title.includes('frontend')) icon = frontendIcon
  if (title.includes('backend')) icon = backendIcon
  return (
    <div
      className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl sm:h-22 sm:w-22"
      style={{ backgroundColor: colors.purple }}
      aria-hidden
    >
      <img src={icon} alt="" className="h-11 w-11 object-contain text-white [filter:brightness(0)_saturate(100%)_invert(100%)]" />
    </div>
  )
}

type CourseCardProps = {
  course: UpskillCourse
  index: number
  isActive: boolean
  setCardRef: (index: number, el: HTMLElement | null) => void
  onEnrollClick: (courseTitle: string) => void
}

const CourseCard = ({ course, index, isActive, setCardRef, onEnrollClick }: CourseCardProps) => {
  const cardRef = useRef<HTMLElement | null>(null)
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'start center'],
  })

  const cardScale = useTransform(scrollYProgress, [0, 1], [0.92, 1])
  const cardY = useTransform(scrollYProgress, [0, 1], [120, 0])

  return (
    <motion.article
      ref={(el) => {
        cardRef.current = el
        setCardRef(index, el)
      }}
      className="sticky mb-[14vh] rounded-2xl border-[5px] px-6 py-6 transition-colors duration-300 sm:rounded-[18px] md:mb-[16vh] md:px-7 md:py-7 lg:mb-[18vh] lg:px-8 lg:py-8 min-[2560px]:mb-[11vh] min-[2560px]:px-12 min-[2560px]:py-10"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.28 }}
      variants={stackItemVariants}
      custom={index}
      style={{
        top: `calc(5.8rem + ${index * 0.9}rem)`,
        zIndex: index + 1,
        borderColor: isActive ? colors.purple : 'transparent',
        scale: cardScale,
        y: cardY,
        transformOrigin: 'center top',
        backgroundColor: colors.upskillCourseCardBg,
        backgroundImage: `linear-gradient(180deg, ${colors.upskillCourseCardBg} 0%, ${colors.upskillCourseCardBgEnd} 100%)`,
        boxShadow: `0 10px 34px ${colors.upskillCourseCardShadow}`,
      }}
    >
      <div className="flex flex-col gap-6 md:gap-7">
        <div className="grid grid-cols-1 lg:gap-4 xl:gap-6 lg:grid-cols-[0.72fr_1.18fr] lg:items-start">
          <div className="flex flex-col items-start gap-5 py-2 pl-3 md:py-3 md:pl-4 lg:py-4 lg:pl-5 min-[2560px]:gap-7 min-[2560px]:py-6 min-[2560px]:pl-8">
  <div
  className="relative inline-flex items-center px-6 py-2 min-[2560px]:px-10 min-[2560px]:py-3"
  style={{
    background: `linear-gradient(90deg, ${colors.upskillStatusGradientStart} 0%, ${colors.upskillStatusGradientEnd} 100%)`,
    clipPath: 'polygon(12px 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0% 50%)',
  }}
>
  <p
    className="relative z-10 font-gilroy-semibold text-[1.2rem] leading-none md:text-[1.4rem] min-[2560px]:text-[2rem]"
    style={{ color: colors.white }}
  >
    {course.status}
  </p>
  {/* Sparkles */}
  <span aria-hidden className="absolute right-8 top-1 text-white/90 text-[0.55rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-5 top-3 text-white/60 text-[0.35rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-6 bottom-1 text-white/50 text-[0.3rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-3 bottom-0.5 text-white/70 text-[0.4rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-10 bottom-0.5 text-white/40 text-[0.28rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-4 bottom-2 text-white/55 text-[0.32rem] leading-none select-none">✦</span>
  <span aria-hidden className="absolute right-9 bottom-1.5 text-white/45 text-[0.25rem] leading-none select-none">✦</span>
</div>
            <CourseIcon course={course} />
            <div className="min-w-0 py-3 text-left">
              <h2 className="relative z-20 font-rajdhani text-[2.3rem] font-semibold leading-[1.0] text-white sm:text-[2.45rem] md:text-[2.6rem] min-[2560px]:text-[3.6rem]">
                {course.title}
              </h2>
              <p className="mt-2 max-w-[500px] font-gilroy-medium text-[1rem] leading-[1.0] text-white/74 min-[2560px]:max-w-[760px] min-[2560px]:text-[2.2rem]">
                {course.subtitle}
              </p>
              <p className="mt-4 font-gilroy-semibold text-[1.2rem] leading-none text-white min-[2560px]:text-[2.2rem]">{course.weeks}</p>
              <p className="mt-2 font-gilroy-medium text-[1.2rem] leading-none text-white/65 min-[2560px]:text-[1.5rem]">{course.level}</p>
            </div>
          </div>

          <div className="pl-2 text-left md:pl-3 min-[2560px]:pl-8">
            <div className="flex items-center justify-between">
              <p className="font-gilroy-semibold text-[1.6rem] text-white min-[2560px]:text-[2.4rem]">{upskillCoursesLabels.masteryHeading}</p>
              <div className="flex shrink-0 items-center gap-1.5">
                <img src={starIcon} alt="" className="h-5 w-5 object-contain min-[2560px]:h-8 min-[2560px]:w-8" aria-hidden />
                <span className="font-gilroy-semibold text-[1.2rem] leading-none text-white min-[2560px]:text-[2.8rem]">{course.rating}</span>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5 pl-1 font-gilroy-medium text-[1rem] leading-[1.32] text-white/66 min-[2560px]:mt-5 min-[2560px]:space-y-2.5 min-[2560px]:text-[2rem]">
              {course.masteryPoints.map((point) => (
                <li key={point} className="flex items-center gap-2">
                  <span className="relative top-[-2px] h-1 w-1 shrink-0 rounded-full bg-white/66" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2 min-[2560px]:mt-10 min-[2560px]:gap-9">
              <div className="text-left">
                <p
                  className="font-gilroy-bold text-[1.75rem] leading-none min-[2560px]:text-[2.7rem]"
                  style={{
                    backgroundImage: `linear-gradient(92.21deg, ${colors.upskillStatusGradientStart} 0%, ${colors.upskillStatusGradientEnd} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {course.pkg}
                </p>
                <p className="mt-2 font-gilroy-medium text-[1.05rem] text-white/68 min-[2560px]:text-[1.5rem]">{upskillCoursesLabels.package}</p>
              </div>
              <div className="text-left">
                <p
                  className="font-gilroy-bold text-[1.75rem] leading-none min-[2560px]:text-[2.7rem]"
                  style={{
                    backgroundImage: `linear-gradient(92.21deg, ${colors.upskillStatusGradientStart} 0%, ${colors.upskillStatusGradientEnd} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {course.projects}
                </p>
                <p className="mt-2 font-gilroy-medium text-[1.05rem] text-white/68 min-[2560px]:text-[1.5rem]">{upskillCoursesLabels.projects}</p>
              </div>
              <div className="flex items-end justify-start">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-transparent px-4 py-2.5 font-gilroy-semibold text-[1rem] leading-none text-white transition-all duration-300 ease-out min-[2560px]:px-11 min-[2560px]:py-4"
                  style={{
                    background: colors.upskillEnrollButtonBg,
                  }}
                  onClick={() => onEnrollClick(course.title)}
                >
                  <span>Enroll Now</span>
                  <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  )
}

const UpskillCoursesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false)
  const [enrollForm, setEnrollForm] = useState<EnrollFormData>({
    name: '',
    phone: '',
    email: '',
    course: upskillCourseTitles[0] ?? '',
    state: '',
    city: '',
    message: '',
  })
  const [enrollErrors, setEnrollErrors] = useState<EnrollFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isSelectActive, setIsSelectActive] = useState(false)
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false)
  const stateDropdownRef = useRef<HTMLDivElement | null>(null)
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false)
  const courseDropdownRef = useRef<HTMLDivElement | null>(null)
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false)
  const cityDropdownRef = useRef<HTMLDivElement | null>(null)
  const [stateSearch, setStateSearch] = useState('')
  const [citySearch, setCitySearch] = useState('')
  const stateSearchRef = useRef<HTMLInputElement | null>(null)
  const citySearchRef = useRef<HTMLInputElement | null>(null)
  const cardRefs = useRef<(HTMLElement | null)[]>([])
  const stateButtonRef = useRef<HTMLButtonElement | null>(null)
  const cityButtonRef = useRef<HTMLButtonElement | null>(null)
  const courseButtonRef = useRef<HTMLButtonElement | null>(null)
  const cityOptions = enrollForm.state ? City.getCitiesOfState('IN', enrollForm.state) : []

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const validateEnrollForm = (form = enrollForm) => {
    const nextErrors: EnrollFormErrors = {}

    if (!form.name.trim()) nextErrors.name = 'Name is required'
    if (!form.phone.trim()) nextErrors.phone = 'Phone number is required'
    else if (!/^\d{10}$/.test(form.phone.trim())) nextErrors.phone = 'Phone number must be exactly 10 digits'
    if (!form.email.trim()) nextErrors.email = 'Email is required'
    else if (!emailPattern.test(form.email.trim())) nextErrors.email = 'Email is not valid'
    if (!form.course.trim()) nextErrors.course = 'Course is required'
    if (!form.state.trim()) nextErrors.state = 'State is required'
    if (!form.city.trim()) nextErrors.city = 'City is required'

    return nextErrors
  }

  const setCardRef = (index: number, el: HTMLElement | null) => {
    cardRefs.current[index] = el
  }

  const openEnrollModal = (courseTitle: string) => {
    setEnrollForm({
      name: '',
      phone: '',
      email: '',
      course: courseTitle,
      state: '',
      city: '',
      message: '',
    })
    setEnrollErrors({})
    setIsEnrollModalOpen(true)
  }

  const closeEnrollModal = () => {
    setIsEnrollModalOpen(false)
  }

  const handleEnrollSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitMessage(null)
    const nextErrors = validateEnrollForm()
    setEnrollErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    submitEnrollForm()
  }

const submitEnrollForm = async () => {
  try {
    setIsSubmitting(true)
    setSubmitMessage(null)

    const appScriptUrl = process.env.REACT_APP_ENROLL_FORM_ENDPOINT || ''
    if (!appScriptUrl) {
      throw new Error('Enroll endpoint is not configured')
    }

        const formData = new FormData()
      formData.append('name', enrollForm.name)
      formData.append('email', enrollForm.email)
      formData.append('phone', enrollForm.phone)
      formData.append('state', indianStates.find((s) => s.isoCode === enrollForm.state)?.name || enrollForm.state)
      formData.append('city', enrollForm.city)
      formData.append('course', enrollForm.course)
      formData.append('message', enrollForm.message)

      const response = await fetch(appScriptUrl, {
      method: 'POST',
      mode: 'no-cors',
        body: formData,
    })

      if (response.status === 0 || response.ok) {
    setSubmitMessage({ type: 'success', text: 'Enrollment submitted successfully! Our counselor will contact you within 24 hours.' })
    toast.success('Enrollment submitted successfully! Our counselor will contact you within 24 hours.')
    setEnrollForm({
      name: '',
      phone: '',
      email: '',
      course: upskillCourseTitles[0] ?? '',
      state: '',
      city: '',
      message: '',
    })
    setEnrollErrors({})

    setTimeout(() => {
      setIsEnrollModalOpen(false)
      setSubmitMessage(null)
    }, 1500)
      } else {
        setSubmitMessage({ type: 'error', text: 'Submission failed. Please try again.' })
      }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Submission failed. Please check your connection.'
    setSubmitMessage({ type: 'error', text: message })
  } finally {
    setIsSubmitting(false)
  }
}

  useEffect(() => {
    const updateActiveCard = () => {
      let nextActive = 0

      cardRefs.current.forEach((el, index) => {
        if (!el) return
        const rect = el.getBoundingClientRect()
        const stickyTop = parseFloat(window.getComputedStyle(el).top || '0')

        if (rect.top <= stickyTop + 2) {
          nextActive = index
        }
      })

      setActiveIndex(nextActive)
    }

    updateActiveCard()
    window.addEventListener('scroll', updateActiveCard, { passive: true })
    window.addEventListener('resize', updateActiveCard)

    return () => {
      window.removeEventListener('scroll', updateActiveCard)
      window.removeEventListener('resize', updateActiveCard)
    }
  }, [])

  useEffect(() => {
    if (!isEnrollModalOpen) return

    const scrollY = window.scrollY
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    const previousBodyPosition = document.body.style.position
    const previousBodyTop = document.body.style.top
    const previousBodyLeft = document.body.style.left
    const previousBodyRight = document.body.style.right
    const previousBodyWidth = document.body.style.width

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
      document.body.style.position = previousBodyPosition
      document.body.style.top = previousBodyTop
      document.body.style.left = previousBodyLeft
      document.body.style.right = previousBodyRight
      document.body.style.width = previousBodyWidth
      window.scrollTo(0, scrollY)
    }
  }, [isEnrollModalOpen])

  useEffect(() => {
    if (!isEnrollModalOpen || !isSelectActive) return

    const preventScroll = (e: Event) => {
      // prevent page/modal from scrolling while native select dropdown is open
      e.preventDefault()
    }

    window.addEventListener('wheel', preventScroll, { passive: false })
    window.addEventListener('touchmove', preventScroll, { passive: false })

    return () => {
      window.removeEventListener('wheel', preventScroll)
      window.removeEventListener('touchmove', preventScroll)
    }
  }, [isEnrollModalOpen, isSelectActive])

  const filteredIndianStates = indianStates.filter((s) => s.name.toLowerCase().includes(stateSearch.trim().toLowerCase()))
  const filteredCityOptions = cityOptions.filter((c) => c.name.toLowerCase().includes(citySearch.trim().toLowerCase()))

  useEffect(() => {
    if (stateDropdownOpen) {
      setTimeout(() => stateSearchRef.current?.focus(), 50)
    }
  }, [stateDropdownOpen])

  useEffect(() => {
    if (cityDropdownOpen) {
      setTimeout(() => citySearchRef.current?.focus(), 50)
    }
  }, [cityDropdownOpen])

  useEffect(() => {
    if (!stateDropdownOpen) return

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        stateDropdownRef.current &&
        stateButtonRef.current &&
        !stateDropdownRef.current.contains(target) &&
        !stateButtonRef.current.contains(target)
      ) {
        setStateDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setStateDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [stateDropdownOpen])

  useEffect(() => {
    if (!cityDropdownOpen) return

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        cityDropdownRef.current &&
        cityButtonRef.current &&
        !cityDropdownRef.current.contains(target) &&
        !cityButtonRef.current.contains(target)
      ) {
        setCityDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCityDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [cityDropdownOpen])

  useEffect(() => {
    if (!courseDropdownOpen) return

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        courseDropdownRef.current &&
        courseButtonRef.current &&
        !courseDropdownRef.current.contains(target) &&
        !courseButtonRef.current.contains(target)
      ) {
        setCourseDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCourseDropdownOpen(false)
        setIsSelectActive(false)
      }
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [courseDropdownOpen])

  return (
    <section
      className="relative w-full px-4 pb-14 pt-8 sm:px-6 md:px-10 lg:px-16 lg:pb-16 min-[2560px]:pb-8"
      style={{ backgroundColor: colors.footerDark }}
    >
      <div className="mx-auto flex w-full max-w-[1180px] flex-col min-[2560px]:max-w-[1640px]">
        {upskillCourses.map((course, index) => (
          <CourseCard
            key={course.id}
            course={course}
            index={index}
            isActive={index === activeIndex}
            setCardRef={setCardRef}
            onEnrollClick={openEnrollModal}
          />
        ))}
        <div className="h-[7vh] md:h-[8vh] lg:h-[9vh] min-[2560px]:h-[4vh]" aria-hidden />
      </div>

      {isEnrollModalOpen && (
        <>
          <style>
            {`
              .upskill-enroll-scroll {
                scrollbar-width: thin;
                scrollbar-color: ${colors.purple} ${colors.upskillScrollbarTrack};
              }
              .upskill-enroll-scroll::-webkit-scrollbar {
                width: 10px;
              }
              .upskill-enroll-scroll::-webkit-scrollbar-track {
                background: ${colors.upskillScrollbarTrack};
                border-radius: 999px;
              }
              .upskill-enroll-scroll::-webkit-scrollbar-thumb {
                background: ${colors.purple};
                border-radius: 999px;
              }
            `}
          </style>
          <div
            className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-4"
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
          >
            <div
              className="relative flex max-h-[90vh] w-full max-w-[820px] flex-col overflow-hidden rounded-[28px] border p-5 md:p-8"
              data-lenis-prevent
              data-lenis-prevent-wheel
              data-lenis-prevent-touch
              style={{ overscrollBehavior: 'contain', borderColor: colors.upskillModalBorder, backgroundColor: colors.upskillModalBg }}
            >
              <button
                type="button"
                className="absolute right-6 top-5 text-[2rem] leading-none text-white/80 hover:text-white"
                onClick={closeEnrollModal}
              >
                ×
              </button>

              <div className="pr-10">
                <h2 className="font-rajdhani text-[2.8rem] font-semibold text-white md:text-[3.2rem]">Enroll Now</h2>
                <p className="mt-1 font-gilroy-medium text-[1.05rem] text-white/70 md:text-[1.15rem]">
                  Start your journey toward success!
                </p>
              </div>

              <div className="mt-4 h-px w-full bg-white/20" />

              <div
                className="upskill-enroll-scroll mt-5 font-gilroy-medium overflow-y-auto overscroll-contain pr-2"
                data-lenis-prevent
                style={{ overscrollBehavior: 'contain' }}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <form className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2" noValidate onSubmit={handleEnrollSubmit}>
                  <div>
                    <label className="mb-2 block text-[1rem] text-white">Name *</label>
                    <input
                      className={`h-14 w-full rounded-xl border bg-white/[0.08] px-4  text-white outline-none placeholder:text-white/45 ${
                        enrollErrors.name ? 'border-red-400' : 'border-white/10'
                      }`}
                      placeholder="Your Name"
                      value={enrollForm.name}
                      onChange={(event) => {
                        const value = event.target.value.replace(/[^A-Za-z\s]/g, '')
                        setEnrollForm((previous) => ({ ...previous, name: value }))
                        setEnrollErrors((previous) => ({ ...previous, name: undefined }))
                      }}
                    />
                    {enrollErrors.name && <p className="mt-1 text-xs text-red-300">{enrollErrors.name}</p>}
                  </div>

                  <div>
                    <label className="mb-2 block  text-[1rem] text-white">Phone Number *</label>
                    <input
                      className={`h-14 w-full rounded-xl border bg-white/[0.08] px-4  text-white outline-none placeholder:text-white/45 ${
                        enrollErrors.phone ? 'border-red-400' : 'border-white/10'
                      }`}
                      placeholder="Your Phone Number"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={enrollForm.phone}
                      onChange={(event) => {
                        const value = event.target.value.replace(/\D/g, '').slice(0, 10)
                        setEnrollForm((previous) => ({ ...previous, phone: value }))
                        setEnrollErrors((previous) => ({ ...previous, phone: undefined }))
                      }}
                    />
                    {enrollErrors.phone && <p className="mt-1 text-xs text-red-300">{enrollErrors.phone}</p>}
                  </div>

                  <div>
                    <label className="mb-2 block text-[1rem] text-white">Email *</label>
                    <input
                      className={`h-14 w-full rounded-xl border bg-white/[0.08] px-4  text-white outline-none placeholder:text-white/45 ${
                        enrollErrors.email ? 'border-red-400' : 'border-white/10'
                      }`}
                      placeholder="Your Email"
                      type="email"
                      value={enrollForm.email}
                      onChange={(event) => {
                        const value = event.target.value
                        setEnrollForm((previous) => ({ ...previous, email: value }))
                        setEnrollErrors((previous) => ({ ...previous, email: undefined }))
                      }}
                    />
                    {enrollErrors.email && <p className="mt-1 text-xs text-red-300">{enrollErrors.email}</p>}
                  </div>

                  <div>
                    <label className="mb-2 block text-[1rem] text-white">Select Course *</label>
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={courseDropdownOpen}
                        className={`flex h-14 w-full items-center justify-between rounded-xl border bg-white/[0.08] px-4 text-left text-white outline-none ${
                          enrollErrors.course ? 'border-red-400' : 'border-white/10'
                        }`}
                        onClick={() => {
                          setCourseDropdownOpen((s) => !s)
                          setIsSelectActive(true)
                        }}
                        ref={courseButtonRef}
                      >
                        <span className={`${enrollForm.course ? '' : 'text-white/45'}`}>{enrollForm.course || 'Select Course'}</span>
                        <img src={GroupIcon} alt="" className="h-4 w-4" />
                      </button>

                      {courseDropdownOpen && (
                        <div
                          ref={courseDropdownRef}
                          role="listbox"
                          tabIndex={-1}
                          className="absolute left-0 top-[calc(100%+6px)] z-30 max-h-60 w-full overflow-y-auto rounded-xl border border-white/10 p-2 shadow-lg"
                          style={{ backgroundColor: colors.upskillDropdownBg }}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          <ul className="space-y-1">
                            {upskillCourseTitles.map((courseTitle) => (
                              <li key={courseTitle}>
                                <button
                                  type="button"
                                  className="w-full rounded px-3 py-2 text-left text-white hover:bg-white/5"
                                  onClick={() => {
                                    setEnrollForm((previous) => ({ ...previous, course: courseTitle }))
                                    setEnrollErrors((previous) => ({ ...previous, course: undefined }))
                                    setCourseDropdownOpen(false)
                                    setIsSelectActive(false)
                                  }}
                                >
                                  {courseTitle}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {enrollErrors.course && <p className="mt-1 text-xs text-red-300">{enrollErrors.course}</p>}
                  </div>

                  <div>
                    <label className="mb-2 block text-[1rem] text-white">State *</label>
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={stateDropdownOpen}
                        className={`flex h-14 w-full items-center justify-between rounded-xl border bg-white/[0.08] px-4 text-left text-white outline-none ${
                          enrollErrors.state ? 'border-red-400' : 'border-white/10'
                        }`}
                        onClick={() => {
                          setStateDropdownOpen((s) => !s)
                          setIsSelectActive(true)
                        }}
                        ref={stateButtonRef}
                      >
                        <span className={`${enrollForm.state ? '' : 'text-white/45'}`}>
                          {enrollForm.state ? indianStates.find((s) => s.isoCode === enrollForm.state)?.name : 'Select State'}
                        </span>
                        <img src={GroupIcon} alt="" className="h-4 w-4" />
                      </button>

                      {stateDropdownOpen && (
                        <div
                          ref={stateDropdownRef}
                          role="listbox"
                          tabIndex={-1}
                          className="absolute left-0 top-[calc(100%+6px)] z-30 max-h-72 w-full overflow-y-auto rounded-xl border border-white/10 p-2 shadow-lg"
                          style={{ backgroundColor: colors.upskillDropdownBg }}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          <div className="px-2 pb-2">
                            <input
                              ref={stateSearchRef}
                              type="text"
                              value={stateSearch}
                              onChange={(e) => setStateSearch(e.target.value)}
                              placeholder="Search state..."
                              className="h-10 w-full rounded-md px-3 text-white placeholder:text-white/45 outline-none"
                              style={{ backgroundColor: colors.upskillSearchBg }}
                            />
                          </div>
                          <ul className="space-y-1 px-1 pb-1">
                            {filteredIndianStates.map((state) => (
                              <li key={state.isoCode}>
                                <button
                                  type="button"
                                  className="w-full rounded px-3 py-2 text-left text-white hover:bg-white/5"
                                  onClick={() => {
                                    setEnrollForm((previous) => ({ ...previous, state: state.isoCode, city: '' }))
                                    setEnrollErrors((previous) => ({ ...previous, state: undefined }))
                                    setStateDropdownOpen(false)
                                    setIsSelectActive(false)
                                    setStateSearch('')
                                    setCitySearch('')
                                  }}
                                >
                                  {state.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {enrollErrors.state && <p className="mt-1 text-xs text-red-300">{enrollErrors.state}</p>}
                  </div>

                  <div>
                    <label className="mb-2 block text-[1rem] text-white">City *</label>
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={cityDropdownOpen}
                        className={`flex h-14 w-full items-center justify-between rounded-xl border bg-white/[0.08] px-4 text-left text-white outline-none ${
                          enrollErrors.city ? 'border-red-400' : 'border-white/10'
                        }`}
                        onClick={() => {
                          if (!enrollForm.state) return
                          setCityDropdownOpen((s) => !s)
                          setIsSelectActive(true)
                        }}
                        ref={cityButtonRef}
                      >
                        <span className={`${enrollForm.city ? '' : 'text-white/45'}`}>
                          {enrollForm.state ? enrollForm.city || 'Select City' : 'Select State First'}
                        </span>
                        <img src={GroupIcon} alt="" className="h-4 w-4" />
                      </button>

                      {cityDropdownOpen && (
                        <div
                          ref={cityDropdownRef}
                          role="listbox"
                          tabIndex={-1}
                          className="absolute left-0 top-[calc(100%+6px)] z-30 max-h-60 w-full overflow-y-auto rounded-xl border border-white/10 p-2 shadow-lg"
                          style={{ backgroundColor: colors.upskillDropdownBg }}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          <div className="px-2 pb-2">
                            <input
                              ref={citySearchRef}
                              type="text"
                              value={citySearch}
                              onChange={(e) => setCitySearch(e.target.value)}
                              placeholder="Search city..."
                              className="h-10 w-full rounded-md px-3 text-white placeholder:text-white/45 outline-none"
                              style={{ backgroundColor: colors.upskillSearchBg }}
                            />
                          </div>
                          <ul className="space-y-1 px-1 pb-1">
                            {filteredCityOptions.map((city) => (
                              <li key={city.name}>
                                <button
                                  type="button"
                                  className="w-full rounded px-3 py-2 text-left text-white hover:bg-white/5"
                                  onClick={() => {
                                    setEnrollForm((previous) => ({ ...previous, city: city.name }))
                                    setEnrollErrors((previous) => ({ ...previous, city: undefined }))
                                    setCityDropdownOpen(false)
                                    setIsSelectActive(false)
                                    setCitySearch('')
                                  }}
                                >
                                  {city.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {enrollErrors.city && <p className="mt-1 text-xs text-red-300">{enrollErrors.city}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-[1rem] text-white">Message</label>
                    <textarea
                      className="min-h-[140px] w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-3 text-white outline-none placeholder:text-white/45"
                      placeholder="Start your journey toward success!"
                      value={enrollForm.message}
                      onChange={(event) => setEnrollForm((previous) => ({ ...previous, message: event.target.value }))}
                    />
                  </div>

                  <div className="md:col-span-2 rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-4 text-[1rem] leading-[1.45] text-white/75">
                    <span className="mr-2" style={{ color: colors.upskillCheckAccent }}>✓</span>
                    <strong>What happens next?</strong> Our career counselor will contact you within 24 hours to discuss your goals and customize your learning path.
                  </div>

                  <div className="md:col-span-2 pt-1">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-14 w-full rounded-xl text-[1.35rem] text-white font-gilroy-bold transition-all duration-300 hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: colors.purple }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>

                  <p className="md:col-span-2 text-center text-[0.95rem] text-white/75">
                    🔒 Your information is secure and will never be shared
                  </p>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default UpskillCoursesSection
